/** Versioned Experience pricing policy shared by admin and guest presentation. */
export type ReservationCharges = {
  mode: "none" | "required" | "optional";
  rateBasisPoints: number;
  serviceChargeTaxable: boolean;
};

export type ReservationChargeSnapshot = ReservationCharges & {
  baseAmount: number;
  serviceChargeAmount: number;
  gratuityAmount: number;
};

/** Frozen charge bases include both configured discounts and staff comps. */
export function reservationReceiptReduction(input: {
  unitAmount: number;
  partySize: number;
  chargeSnapshot?: ReservationChargeSnapshot;
  discountAmount?: number;
}): number {
  return input.chargeSnapshot
    ? Math.max(0, input.unitAmount * input.partySize - input.chargeSnapshot.baseAmount)
    : input.discountAmount ?? 0;
}

export function reservationCharges(policy: Record<string, unknown>): ReservationCharges {
  const value = policy.charges as Partial<ReservationCharges> | undefined;
  return {
    mode: value?.mode ?? "none",
    rateBasisPoints: value?.rateBasisPoints ?? 0,
    serviceChargeTaxable: value?.serviceChargeTaxable ?? false,
  };
}

export function reservationChargesError(policy: Record<string, unknown>, bookingMethod: string): string | null {
  const charges = reservationCharges(policy);
  if (!["none", "required", "optional"].includes(charges.mode)) return "Choose a valid service-charge or gratuity option.";
  if (!Number.isInteger(charges.rateBasisPoints) || charges.rateBasisPoints < 0 || charges.rateBasisPoints > 10000) return "Enter a percentage between 0 and 100, with at most two decimal places.";
  if (charges.mode === "none" && (charges.rateBasisPoints !== 0 || charges.serviceChargeTaxable)) return "Remove the percentage and tax setting when no charge is selected.";
  if (charges.mode === "optional" && charges.serviceChargeTaxable) return "Optional gratuity cannot use the service-charge tax setting.";
  if (charges.mode !== "none" && bookingMethod !== "prepaid") return "Service charges and optional gratuity require a prepaid experience. Remove the charge or select Prepaid.";
  return null;
}

/** Reject malformed/overprecise input instead of silently rounding a guest's choice. */
export function gratuityBasisPoints(percent: string): number | null {
  if (!/^\d{1,3}(?:\.\d{1,2})?$/.test(percent.trim())) return null;
  const amount = Math.round(Number(percent) * 100);
  return amount <= 10000 ? amount : null;
}
